import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../api";
import { useLanguage } from "../../i18n/LanguageContext";
import "../Login/Login.css";

// Forgot password: the server always answers the same way, so this page never reveals whether an account exists.
const Shell = ({ title, children }) => {
  const { t, language, toggleLanguage } = useLanguage();
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-language-row">
          <button type="button" className="login-language-btn" onClick={toggleLanguage}>{language === "sw" ? "English" : "Kiswahili"}</button>
        </div>
        <div className="login-header">
          <h1 className="login-title">{t(title)}</h1>
          <p className="login-subtitle">{t("TAREWU Management System")}</p>
        </div>
        {children}
        <p className="login-back-link"><Link to="/login">← {t("Back to sign in")}</Link></p>
      </div>
    </div>
  );
};

export const ForgotPassword = () => {
  const { t } = useLanguage();
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api.forgotPassword(identifier.trim());
      setDone(res?.message || "");
    } catch (err) {
      setError(t(err.message || "Something went wrong. Try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Forgot password">
      {done ? (
        <div className="login-form">
          <div className="success-message">{t(done)}</div>
          <p className="login-help">{t("Did not receive it? Check that you used the email or phone number registered with TAREWU, wait two minutes and try again, or contact the TAREWU office.")}</p>
        </div>
      ) : (
        <form onSubmit={submit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          <p className="login-help">{t("Enter the email you sign in with, or your registered phone number. We will send you a link to set a new password.")}</p>
          <div className="form-group">
            <label className="form-label">{t("Email or phone number")}</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">@</span>
              <input className="form-input" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required autoComplete="username" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary login-btn" disabled={busy || !identifier.trim()}>{busy ? t("Please wait...") : t("Send reset link")}</button>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError(t("Use a password with at least 8 characters."));
    if (password !== confirm) return setError(t("The two passwords do not match."));
    setBusy(true);
    try {
      const res = await api.resetPassword(token, password);
      setDone(res?.message || "");
    } catch (err) {
      setError(t(err.message || "Something went wrong. Try again."));
    } finally {
      setBusy(false);
    }
  };
  if (!token) {
    return (
      <Shell title="Set a new password">
        <div className="login-form"><div className="error-message">{t("This reset link is invalid or has expired. Ask for a new one.")}</div>
          <p className="login-back-link"><Link to="/forgot-password">{t("Ask for a new link")}</Link></p></div>
      </Shell>
    );
  }
  return (
    <Shell title="Set a new password">
      {done ? (
        <div className="login-form">
          <div className="success-message">{t(done)}</div>
          <Link to="/login" className="btn btn-primary login-btn">{t("Sign In")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          <div className="form-group">
            <label className="form-label">{t("New password")}</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">●</span>
              <input className="form-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} required autoComplete="new-password" />
              <button type="button" className="password-toggle" onClick={() => setShow(!show)}>{show ? t("Hide") : t("Show")}</button>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">{t("Confirm new password")}</label>
            <div className="login-input-wrap">
              <span className="login-input-icon">●</span>
              <input className="form-input" type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} minLength={8} required autoComplete="new-password" />
            </div>
          </div>
          {error.includes("expired") || error.includes("muda wake") ? <p className="login-back-link"><Link to="/forgot-password">{t("Ask for a new link")}</Link></p> : null}
          <button type="submit" className="btn btn-primary login-btn" disabled={busy}>{busy ? t("Please wait...") : t("Save new password")}</button>
        </form>
      )}
    </Shell>
  );
};
